import styles from './home/home.module.css';
// next-session-banner.tsx — the gold "Up Next" banner for the next campaign
// gameplay session (the one that will happen or is happening now). Rendered on
// the home and the campaigns list; renders nothing when there is none.
//
// Three click behaviours, by reachability:
//  - open        → straight to the activity
//  - pending     → the chapter holding the required activities (+ warning)
//  - date-locked → the campaign's chapter list (+ "chapter opens …" warning)
import { Label } from '../../components/label';
import { useNowSeconds } from '../../utils/use-now-seconds';
import { useTranslation } from 'react-i18next';
import { useNextCampaignGameplay } from '../../api/student/campaigns/hooks';
import type { NextGameplayState } from '../../api/student/campaigns/gating';
import type { StudentNavigate } from './student-location';

const pad = (n: number) => String(Math.max(0, n)).padStart(2, '0');

/** What the banner's click does, said beside its arrow, by how the session can be reached: keys written whole. */
const CTA_KEY: Record<NextGameplayState, string> = {
  open: 'student.home.upNext.joinSession',
  pending: 'student.home.upNext.openChapterCta',
  'date-locked': 'student.home.upNext.viewChaptersCta',
};

export function NextSessionBanner({ navigate }: { navigate: StudentNavigate }) {
  const { t, i18n } = useTranslation();
  const next = useNextCampaignGameplay();

  // Live 1s tick so the countdown animates while the page is open.
  const now = useNowSeconds() * 1000;

  if (!next) return null;

  const sessionTimeLabel = new Intl.DateTimeFormat(i18n.language, {
    weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  }).format(new Date(next.startTimeUtc * 1000));
  const chapterDateLabel = next.chapterStartDateUtc
    ? new Intl.DateTimeFormat(i18n.language, { day: '2-digit', month: 'short' }).format(new Date(next.chapterStartDateUtc * 1000))
    : null;

  const cdMs = next.startTimeUtc * 1000 - now;
  const cd = {
    d: Math.floor(cdMs / 86400000),
    h: Math.floor((cdMs % 86400000) / 3600000),
    m: Math.floor((cdMs % 3600000) / 60000),
    s: Math.floor((cdMs % 60000) / 1000),
  };

  const handleClick = () => {
    if (next.state === 'pending') {
      navigate({ kind: 'chapter', campaignId: next.campaignId, chapterId: next.targetChapterId });
    } else if (next.state === 'date-locked') {
      navigate({ kind: 'campaign', campaignId: next.campaignId });
    } else {
      navigate({ kind: 'activity', campaignId: next.campaignId, chapterId: next.chapterId, activityId: next.activityId });
    }
  };

  return (
    <button type="button" className={`${styles.session} framed reveal`} onClick={handleClick}>
      <div className={`${styles.session__icon}`}><span /></div>
      <div className={`${styles.session__main}`}>
        <Label>{t('student.home.upNext.title')}</Label>
        <h2 className={`${styles.session__title}`}>{next.activityTitle}</h2>
        <p className={`${styles.session__meta}`}>{next.campaignName} · {sessionTimeLabel}</p>
        {next.state === 'pending' && (
          <span className={`${styles.session__warn}`}>⚠ {t('student.home.upNext.lockedActivities', { count: next.pendingCount })}</span>
        )}
        {next.state === 'date-locked' && (
          <span className={`${styles.session__warn}`}>🔒 {chapterDateLabel
            ? t('student.home.upNext.lockedChapter', { date: chapterDateLabel })
            : t('student.home.upNext.lockedChapterSoon')}</span>
        )}
      </div>
      <div className={`${styles.session__side}`}>
        {cdMs > 0 ? (
          <div className={`${styles.clock}`}>
            <div className={`${styles.clock__u}`}><b>{pad(cd.d)}</b><span>{t('student.home.countdown.days')}</span></div>
            <div className={`${styles.clock__u}`}><b>{pad(cd.h)}</b><span>{t('student.home.countdown.hrs')}</span></div>
            <div className={`${styles.clock__u}`}><b>{pad(cd.m)}</b><span>{t('student.home.countdown.min')}</span></div>
            <div className={`${styles.clock__u}`}><b>{pad(cd.s)}</b><span>{t('student.home.countdown.sec')}</span></div>
          </div>
        ) : (
          <p className={`${styles.session__meta}`}>{t('student.home.upNext.startingNow')}</p>
        )}
        <span className={`${styles.session__cta}`}>{t(CTA_KEY[next.state])} →</span>
      </div>
    </button>
  );
}
